import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';

const QUESTION_TYPES = {
  rating: 'Rating (1–5)',
  multiple_choice: 'Choose the best answer',
  multiple_select: 'Choose all that apply',
  true_false: 'True / False',
  fill_blank: 'Fill in the blank',
  short_answer: 'Short answer'
};

const CHOICE_TYPES = ['multiple_choice', 'multiple_select'];
const RATING_VALUES = ['1', '2', '3', '4', '5'];
const TRUE_FALSE_VALUES = ['True', 'False'];

const emptyForm = {
  text: '',
  type: 'rating',
  program: '',
  options: [{ text: '', marks: 0 }, { text: '', marks: 0 }],
  ratingMarks: [1, 2, 3, 4, 5],
  trueFalseMarks: [1, 0],
  scaleMinLabel: '',
  scaleMaxLabel: '',
  acceptedAnswers: ['']
};

const inputClass = 'w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]';
const marksInputClass = 'w-20 shrink-0 px-2 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-[#1086a4]';

const QuestionBankTab = ({ auth }) => {
  const [questions, setQuestions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [programFilter, setProgramFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const getAuthConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    if (auth.token) config.headers['Authorization'] = 'Bearer ' + auth.token;
    return config;
  };

  const fetchQuestions = async () => {
    try {
      const query = programFilter ? `?program=${programFilter}` : '';
      const res = await axios.get(`/api/program-questions${query}`, getAuthConfig());
      setQuestions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get('/api/programs', getAuthConfig())
      .then(res => setPrograms(res.data))
      .catch(err => console.error(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchQuestions(); }, [programFilter]);

  const openNew = () => {
    setForm({ ...emptyForm, program: programFilter });
    setEditingId(null);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (q) => {
    const marksOf = (o) => o.marks ?? 0;
    setForm({
      ...emptyForm,
      text: q.text,
      type: q.type,
      program: q.program?._id || '',
      options: CHOICE_TYPES.includes(q.type) ? q.options.map(o => ({ text: o.text, marks: marksOf(o) })) : emptyForm.options,
      ratingMarks: q.type === 'rating' ? q.options.map(marksOf) : emptyForm.ratingMarks,
      trueFalseMarks: q.type === 'true_false' ? q.options.map(marksOf) : emptyForm.trueFalseMarks,
      scaleMinLabel: q.scaleMinLabel || '',
      scaleMaxLabel: q.scaleMaxLabel || '',
      acceptedAnswers: q.acceptedAnswers.length > 0 ? q.acceptedAnswers : ['']
    });
    setEditingId(q._id);
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = { text: form.text, type: form.type, program: form.program };
      if (form.type === 'rating') {
        payload.options = RATING_VALUES.map((value, i) => ({ text: value, marks: form.ratingMarks[i] }));
        payload.scaleMinLabel = form.scaleMinLabel;
        payload.scaleMaxLabel = form.scaleMaxLabel;
      } else if (CHOICE_TYPES.includes(form.type)) {
        payload.options = form.options;
      } else if (form.type === 'true_false') {
        payload.options = TRUE_FALSE_VALUES.map((value, i) => ({ text: value, marks: form.trueFalseMarks[i] }));
      } else if (form.type === 'fill_blank') {
        payload.acceptedAnswers = form.acceptedAnswers;
      }

      if (editingId) {
        await axios.put(`/api/program-questions/${editingId}`, payload, getAuthConfig());
      } else {
        await axios.post('/api/program-questions', payload, getAuthConfig());
      }
      closeForm();
      fetchQuestions();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`/api/program-questions/${id}`, getAuthConfig());
      fetchQuestions();
    } catch (err) {
      console.error(err);
    }
  };

  // Option helpers (choose the best answer / choose all that apply)
  const updateOption = (index, field, value) =>
    setForm({ ...form, options: form.options.map((o, i) => i === index ? { ...o, [field]: value } : o) });
  const addOption = () => setForm({ ...form, options: [...form.options, { text: '', marks: 0 }] });
  const removeOption = (index) => setForm({ ...form, options: form.options.filter((_, i) => i !== index) });

  // Marks helpers for fixed answers (rating / true-false)
  const updateFixedMarks = (field, index, value) =>
    setForm({ ...form, [field]: form[field].map((m, i) => i === index ? value : m) });

  // Accepted answer helpers (fill in the blank)
  const updateAnswer = (index, value) =>
    setForm({ ...form, acceptedAnswers: form.acceptedAnswers.map((a, i) => i === index ? value : a) });
  const addAnswer = () => setForm({ ...form, acceptedAnswers: [...form.acceptedAnswers, ''] });
  const removeAnswer = (index) => setForm({ ...form, acceptedAnswers: form.acceptedAnswers.filter((_, i) => i !== index) });

  const renderMarksInput = (value, onChange) => (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      required
      className={marksInputClass}
      placeholder="Marks"
    />
  );

  const renderAnswerSummary = (q) => {
    if (q.type === 'fill_blank') {
      return <p className="text-foreground/60 text-xs mt-2">Accepted: {q.acceptedAnswers.join(', ')}</p>;
    }
    if (q.options.length > 0) {
      return (
        <div className="mt-2">
          {q.type === 'rating' && (q.scaleMinLabel || q.scaleMaxLabel) && (
            <p className="text-foreground/50 text-xs mb-1">
              1 = {q.scaleMinLabel || '—'} · 5 = {q.scaleMaxLabel || '—'}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            {q.options.map((o, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full text-xs bg-muted text-foreground/70">
                {o.text} <span className="text-[#1086a4] font-medium">· {o.marks ?? 0} marks</span>
              </span>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <div className="text-foreground/60 p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-bold text-foreground">Question Bank</h2>
        <div className="flex gap-2">
          <select
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
          >
            <option value="">All programs</option>
            {programs.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
          </select>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#0d6e87] transition-colors text-sm font-medium whitespace-nowrap"
          >
            <FaPlus size={12} /> Add Question
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-lg p-6 border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                {editingId ? 'Edit Question' : 'Add Question'}
              </h3>
              <button onClick={closeForm} className="text-foreground/40 hover:text-foreground/60">
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Program</label>
                <select
                  value={form.program}
                  onChange={(e) => setForm({ ...form, program: e.target.value })}
                  required
                  className={inputClass}
                >
                  <option value="">Select a program</option>
                  {programs.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Question type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className={inputClass}
                >
                  {Object.entries(QUESTION_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Question</label>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  required
                  rows={2}
                  className={inputClass}
                  placeholder={form.type === 'fill_blank' ? 'e.g. The capital of France is ___.' : 'Enter the question...'}
                />
                {form.type === 'fill_blank' && (
                  <p className="text-foreground/40 text-xs mt-1">Use ___ to mark the blank.</p>
                )}
              </div>

              {/* Rating 1–5: marks per value + optional end labels */}
              {form.type === 'rating' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Marks for each answer <span className="text-foreground/40 font-normal">(1 = lowest, 5 = highest)</span>
                  </label>
                  {RATING_VALUES.map((value, i) => (
                    <div key={value} className="flex items-center gap-2 mb-2">
                      <span className="w-8 text-sm font-medium text-foreground text-center">{value}</span>
                      {renderMarksInput(form.ratingMarks[i], (v) => updateFixedMarks('ratingMarks', i, v))}
                      <span className="text-foreground/40 text-xs">marks</span>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      value={form.scaleMinLabel}
                      onChange={(e) => setForm({ ...form, scaleMinLabel: e.target.value })}
                      className={inputClass}
                      placeholder="Label for 1 (optional)"
                    />
                    <input
                      type="text"
                      value={form.scaleMaxLabel}
                      onChange={(e) => setForm({ ...form, scaleMaxLabel: e.target.value })}
                      className={inputClass}
                      placeholder="Label for 5 (optional)"
                    />
                  </div>
                </div>
              )}

              {/* Options with marks: choose the best answer / choose all that apply */}
              {CHOICE_TYPES.includes(form.type) && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Options and marks{' '}
                    <span className="text-foreground/40 font-normal">
                      {form.type === 'multiple_select' ? '(marks of all picked options are added up)' : ''}
                    </span>
                  </label>
                  {form.options.map((option, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => updateOption(i, 'text', e.target.value)}
                        className={inputClass}
                        placeholder={`Option ${i + 1}`}
                      />
                      {renderMarksInput(option.marks, (v) => updateOption(i, 'marks', v))}
                      {form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600 px-1">
                          <FaTimes size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addOption} className="text-[#1086a4] text-sm hover:underline flex items-center gap-1">
                    <FaPlus size={10} /> Add option
                  </button>
                </div>
              )}

              {/* True / False: marks for each */}
              {form.type === 'true_false' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Marks for each answer</label>
                  {TRUE_FALSE_VALUES.map((value, i) => (
                    <div key={value} className="flex items-center gap-2 mb-2">
                      <span className="w-12 text-sm font-medium text-foreground">{value}</span>
                      {renderMarksInput(form.trueFalseMarks[i], (v) => updateFixedMarks('trueFalseMarks', i, v))}
                      <span className="text-foreground/40 text-xs">marks</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Fill in the blank */}
              {form.type === 'fill_blank' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Accepted answers</label>
                  {form.acceptedAnswers.map((answer, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={answer}
                        onChange={(e) => updateAnswer(i, e.target.value)}
                        className={inputClass}
                        placeholder="e.g. Paris"
                      />
                      {form.acceptedAnswers.length > 1 && (
                        <button type="button" onClick={() => removeAnswer(i)} className="text-red-400 hover:text-red-600 px-1">
                          <FaTimes size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addAnswer} className="text-[#1086a4] text-sm hover:underline flex items-center gap-1">
                    <FaPlus size={10} /> Add another accepted answer
                  </button>
                </div>
              )}

              {form.type === 'short_answer' && (
                <p className="text-foreground/50 text-sm">Students type a free-text answer. It is not scored.</p>
              )}

              {formError && <p className="text-red-500 text-sm">{formError}</p>}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-[#1086a4] rounded-lg text-[#1086a4] font-medium hover:bg-[#f9fafb] transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#1086a4] text-white rounded-lg font-medium hover:bg-[#0d6e87] disabled:opacity-50 transition-colors text-sm"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question List */}
      {questions.length === 0 ? (
        <div className="text-center py-12 text-foreground/50">
          <p className="text-lg mb-2">No questions yet</p>
          <p className="text-sm">Add questions and link them to a program.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((q) => (
            <div key={q._id} className="border border-border rounded-lg p-4 bg-background">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-[#1086a4]/10 text-[#1086a4] rounded-full text-xs font-medium">
                      {QUESTION_TYPES[q.type]}
                    </span>
                    <span className="px-2 py-0.5 bg-muted text-foreground/60 rounded-full text-xs">
                      {q.program?.title || 'No program'}
                    </span>
                  </div>
                  <h4 className="font-medium text-foreground text-sm">{q.text}</h4>
                  {renderAnswerSummary(q)}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(q)} className="text-foreground/40 hover:text-[#1086a4] transition-colors">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => handleDelete(q._id)} className="text-foreground/40 hover:text-red-500 transition-colors">
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });
export default connect(mapStateToProps)(QuestionBankTab);
